// @flow

import styled from '@emotion/styled';
import * as React from 'react';
import { flow, pickBy, keys, find, map, sortBy, get } from 'lodash/fp';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import Page from 'app/containers/parts/Page';
import * as rapidfireActions from 'app/actions/rapidfireActions';
import Loader from '../../components/commons/Loader';
import RapidFireLobby from './RapidFireLobby';
import RapidFireTeamSelection from './RapidFireTeamSelection';
import { rapidFireSportIdAssetsMapping } from './constants';
import RapidFireLanding from './RapidFireLanding';

type Props = {
  competitions: Array<Object>,
  competitionId: number,
  contestType?: string,
  section?: string,
  getUpcoming: () => void,
  upcomingChallenges: Array<Object>,
  hasInitialised: boolean
};

const SlideOverflowWrapper = styled.div({
  overflow: 'hidden',
  width: '100%'
});

const SlideContainer = styled.div(
  {
    display: 'flex'
  },
  ({ pageNo, numPages }) => ({
    transform: `translateX(${((pageNo - 1) * -100) / numPages}%)`,
    transition: '0.3s all ease-in-out',
    width: `${numPages * 100}%`
  })
);

const Section = styled.div(
  {
    flexBasis: '50%',
    transition: '0.2s opacity'
  },
  ({ selected }) => ({
    opacity: selected ? 1 : 0
  })
);

const pages = ['lobby', 'enter'];

function isAllowed(section, contestType, selectedChallenge) {
  if (section === 'enter') {
    if (contestType === '3' && selectedChallenge.rf3EnteredChallenge) return false;
    if (contestType === '5' && selectedChallenge.rf5EnteredChallenge) return false;
  }
  return true;
}

class RapidFire extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    contestType: undefined,
    section: undefined
  };

  componentDidMount() {
    const { getUpcoming } = this.props;
    getUpcoming();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // [MC] bit of an ugly hack to fetch the lobby again after you submit your team
    const { section, getUpcoming } = this.props;

    if (section === 'enter' && nextProps.section !== 'enter') {
      getUpcoming();
    }
  }

  render() {
    const {
      competitions,
      upcomingChallenges,
      competitionId,
      contestType,
      hasInitialised,
      section
    } = this.props;

    if (!hasInitialised) {
      return <Loader />;
    }

    if (upcomingChallenges.length === 0) {
      return (
        <Page title="RapidFire">
          <RapidFireLanding
            showHeader
            hasCompetition={
              competitions.filter(item => item && item.rapidfire_enabled && item.rapidfire_enabled)
                .length > 0
            }
          />
        </Page>
      );
    }

    const selectedCompetition = competitions.find(c => c.id === competitionId && c.active);
    if (!selectedCompetition) {
      return <Redirect to={`/rapidfire/${upcomingChallenges[0].competitionId}/3/lobby`} />;
    }
    const selectedChallenge = upcomingChallenges.find(
      ch => ch.competitionId === selectedCompetition.id
    );
    const availableSections = flow(
      pickBy(val => !val),
      keys
    )({
      3: selectedChallenge.rf3EnteredChallenge,
      5: selectedChallenge.rf5EnteredChallenge
    });
    const defaultSection = availableSections[0] || '3';
    const pageNo = pages.indexOf(section) + 1;
    if (
      !contestType ||
      (availableSections.length > 0 && !availableSections.includes(contestType))
    ) {
      return <Redirect to={`/rapidfire/${competitionId}/${defaultSection}/lobby`} />;
    }
    if (!selectedChallenge) {
      return (
        <Redirect
          to={`/rapidfire/${upcomingChallenges[0].competitionId}/${defaultSection}/lobby`}
        />
      );
    }
    if (pageNo === 0 || !isAllowed(section, contestType, selectedChallenge)) {
      return <Redirect to={`/rapidfire/${competitionId}/${contestType}/lobby`} />;
    }

    const entered3PayStructure = selectedChallenge.payStructures
      ? selectedChallenge.payStructures.find(p => p.key === 'rapidfire_3')
      : {};
    const entered5PayStructure = selectedChallenge.payStructures
      ? selectedChallenge.payStructures.find(p => p.key === 'rapidfire_5')
      : {};

    return (
      <Page title="RapidFire">
        <SlideOverflowWrapper>
          <SlideContainer pageNo={pageNo} numPages={2}>
            <Section selected={pageNo === 1}>
              <RapidFireLobby
                competitions={competitions}
                competitionId={competitionId}
                entered5={selectedChallenge.rf5EnteredChallenge}
                entered3={selectedChallenge.rf3EnteredChallenge}
                entered3PayStructure={entered3PayStructure}
                entered5PayStructure={entered5PayStructure}
                contestType={contestType}
                defaultAssets={rapidFireSportIdAssetsMapping[0]}
              />
            </Section>
            <Section selected={pageNo === 2}>
              <RapidFireTeamSelection
                competition={selectedCompetition}
                contestType={contestType}
                backLink={`/rapidfire/${competitionId}/${contestType}/lobby`}
                challengeGroupId={selectedChallenge.id}
                isVisible={pageNo === 2}
              />
            </Section>
          </SlideContainer>
        </SlideOverflowWrapper>
      </Page>
    );
  }
}

function getCompetitionName(comp, competitions) {
  return competitions.find(
    c =>
      c.id !== comp.id &&
      get('competition_type.short_name', c) === get('competition_type.short_name', comp)
  )
    ? comp.name
    : comp.competition_type.short_name;
}

function mapStateToProps(state: GlobalState, { match }) {
  return {
    competitionId: parseInt(match.params.competitionId, 10),
    contestType: match.params.contestType,
    section: match.params.section,
    hasInitialised: state.rapidfire.hasInitialised,
    upcomingChallenges: state.rapidfire.upcomingChallenges,
    competitions: flow(
      map(c => ({
        ...c,
        active: find(challenge => challenge.competitionId === c.id)(
          state.rapidfire.upcomingChallenges
        ),
        assets: rapidFireSportIdAssetsMapping.find(
          asset =>
            asset.sportId === c.sport_id ||
            asset.name === (get('competition_type.short_name', c) || '').toLowerCase()
        ),
        name: getCompetitionName(c, state.rapidfire.competitions),
        images: get('competition_type.images', c)
      })),
      sortBy('active')
    )(state.rapidfire.competitions)
  };
}

const actions = {
  getUpcoming: rapidfireActions.getUpcoming
};

export default connect(mapStateToProps, actions)(RapidFire);
